import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { DownOutlined, FolderOpenOutlined, FolderOutlined, InboxOutlined, RightOutlined } from '@ant-design/icons';

import { Button, Skeleton } from '../../../components/ui';
import { useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { soVN } from '../../../lib/dinh-dang';
import { dungCay, duongToiGoc, traiCay } from '../nghiep-vu/cay-thu-muc';
import { CHUA_PHAN_LOAI } from '../hooks/useThuMuc';
import type { Folder } from '../types';

/**
 * Cây thư mục bên trái màn Tài liệu. Ba mục không phải thư mục và đều cần:
 * - "Tất cả" đứng đầu — trạng thái mặc định, cũng là thứ app di động thấy;
 * - "Chưa phân loại" luôn hiện kể cả khi rỗng: tài liệu chưa xếp nằm ở đây, giấu đi thì không ai dọn.
 *
 * Trải cây thành danh sách phẳng rồi `map` một vòng; thụt lề tính từ độ sâu.
 */
export function CayThuMuc({
  danhSach,
  dangTai,
  loi,
  onTaiLai,
  chon,
  onChon,
  tongTatCa,
  soChuaPhanLoai,
  cuoiCay,
  thaoTac,
}: {
  danhSach: readonly Folder[];
  dangTai: boolean;
  /** Câu lỗi đã dịch. Có thì vẽ nhánh lỗi, **không** vẽ cây rỗng. */
  loi: string | null;
  onTaiLai: () => void;
  /** `undefined` là "Tất cả"; `CHUA_PHAN_LOAI`; hoặc id thư mục. */
  chon: string | undefined;
  onChon: (giaTri: string | undefined) => void;
  tongTatCa?: number;
  soChuaPhanLoai?: number;
  cuoiCay?: ReactNode;
  /** Menu thao tác trên một thư mục; trả `null` khi không có quyền nào. */
  thaoTac?: (f: Folder) => ReactNode;
}) {
  const { t, locale } = useT();
  const cay = useMemo(() => dungCay(danhSach), [danhSach]);

  /*
    Mở sẵn đường tới thư mục đang chọn — người dùng dán link `?thuMuc=<id sâu>` thấy ngay mình đang đứng
    đâu. Mở thêm lúc vẽ (không effect): nhánh chứa mục đang chọn luôn mở, cộng các nhánh người dùng tự mở.
  */
  const [tuMo, setTuMo] = useState<ReadonlySet<string>>(new Set());
  const [tuDong, setTuDong] = useState<ReadonlySet<string>>(new Set());
  const dangMo = useMemo(() => {
    const s = new Set(tuMo);
    if (chon !== undefined && chon !== CHUA_PHAN_LOAI) {
      for (const id of duongToiGoc(danhSach, chon).slice(0, -1)) if (!tuDong.has(id)) s.add(id);
    }
    return s;
  }, [tuMo, tuDong, chon, danhSach]);
  const phang = useMemo(() => traiCay(cay, dangMo), [cay, dangMo]);

  const doiMo = (id: string) => {
    const mo = dangMo.has(id);
    setTuMo((cu) => {
      const moi = new Set(cu);
      if (mo) moi.delete(id);
      else moi.add(id);
      return moi;
    });
    setTuDong((cu) => {
      const moi = new Set(cu);
      if (mo) moi.add(id);
      else moi.delete(id);
      return moi;
    });
  };

  // Lỗi xét trước "đang tải" và trước cây rỗng: 403/500 mà vẽ ra "chưa có thư mục nào" là nói sai.
  if (loi !== null) {
    return (
      <div role="alert" className="space-y-2 p-3">
        <p className="text-xs leading-relaxed text-danger-700">{loi}</p>
        <Button variant="outline" size="sm" onClick={onTaiLai}>
          {t('chung.thuLai')}
        </Button>
      </div>
    );
  }

  if (dangTai) {
    return (
      <div className="space-y-2 p-3" aria-busy="true">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-7 w-full" />
        ))}
      </div>
    );
  }

  return (
    <nav className="flex flex-col gap-0.5 p-2" aria-label={t('baoCaoDoanhNghiep.thuMuc.tieuDe')}>
      <MucCay
        nhan={t('baoCaoDoanhNghiep.thuMuc.tatCa')}
        so={tongTatCa === undefined ? undefined : soVN(tongTatCa, locale)}
        dangChon={chon === undefined}
        onClick={() => onChon(undefined)}
        icon={<FolderOpenOutlined aria-hidden />}
      />

      {danhSach.length === 0 && (
        <p className="px-2 py-2 text-xs leading-relaxed text-content-muted">{t('baoCaoDoanhNghiep.thuMuc.chuaCoThuMuc')}</p>
      )}

      {phang.map((n) => (
        <MucCay
          key={n.id}
          nhan={n.name}
          so={n.documentsTotal === undefined ? undefined : soVN(n.documentsTotal, locale)}
          sau={n.sau}
          dangChon={chon === n.id}
          onClick={() => onChon(n.id)}
          icon={<FolderOutlined aria-hidden />}
          coCon={n.con.length > 0}
          dangMo={dangMo.has(n.id)}
          onDoiMo={() => doiMo(n.id)}
          nhanDoiMo={t(dangMo.has(n.id) ? 'baoCaoDoanhNghiep.thuMuc.thuGon' : 'baoCaoDoanhNghiep.thuMuc.moRong', { ten: n.name })}
          phai={thaoTac?.(n)}
        />
      ))}

      <MucCay
        nhan={t('baoCaoDoanhNghiep.thuMuc.chuaPhanLoai')}
        so={soChuaPhanLoai === undefined ? undefined : soVN(soChuaPhanLoai, locale)}
        dangChon={chon === CHUA_PHAN_LOAI}
        onClick={() => onChon(CHUA_PHAN_LOAI)}
        icon={<InboxOutlined aria-hidden />}
      />

      {cuoiCay && <div className="mt-2 px-1">{cuoiCay}</div>}
    </nav>
  );
}

function MucCay({
  nhan,
  so,
  sau = 0,
  dangChon,
  onClick,
  icon,
  coCon = false,
  dangMo = false,
  onDoiMo,
  nhanDoiMo,
  phai,
}: {
  nhan: string;
  so?: string;
  sau?: number;
  dangChon: boolean;
  onClick: () => void;
  icon: ReactNode;
  coCon?: boolean;
  dangMo?: boolean;
  onDoiMo?: () => void;
  nhanDoiMo?: string;
  phai?: ReactNode;
}) {
  return (
    <div
      // Mục đang chọn: nền nhấn nhạt + chữ đậm bậc -700; `bg-subtle` để dành cho hover.
      className={cn(
        'group flex items-center gap-1 rounded pr-1 text-sm transition-colors',
        dangChon
          ? 'bg-primary-50 font-semibold text-primary-700'
          : 'text-content-secondary hover:bg-subtle hover:text-content-primary',
      )}
      // Độ sâu là dữ liệu lúc chạy, Tailwind không sinh được class động. Padding chứ không margin để vùng
      // bấm phủ hết chiều ngang.
      style={{ paddingInlineStart: `${sau * 16 + 4}px` }}
    >
      {coCon ? (
        <button
          type="button"
          onClick={onDoiMo}
          aria-expanded={dangMo}
          aria-label={nhanDoiMo}
          title={nhanDoiMo}
          className="flex size-6 shrink-0 items-center justify-center rounded text-content-muted transition-colors hover:bg-surface hover:text-content-secondary"
        >
          {dangMo ? <DownOutlined /> : <RightOutlined />}
        </button>
      ) : (
        <span className="size-6 shrink-0" aria-hidden />
      )}

      <button
        type="button"
        onClick={onClick}
        aria-current={dangChon ? 'page' : undefined}
        className="flex min-w-0 flex-1 items-center gap-2 py-1.5 text-left"
        title={nhan}
      >
        <span className="shrink-0 text-content-muted">{icon}</span>
        <span className="truncate">{nhan}</span>
        {so !== undefined && <span className="ml-auto shrink-0 pl-2 text-xs tabular-nums text-content-muted">{so}</span>}
      </button>

      {phai && (
        // Hiện khi rê chuột hoặc có focus bàn phím; luôn hiện ở mục đang chọn (màn cảm ứng không có hover).
        <span
          className={cn(
            'shrink-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100',
            dangChon ? 'opacity-100' : 'opacity-0',
          )}
        >
          {phai}
        </span>
      )}
    </div>
  );
}
